import { Ref } from '@nuxtjs/composition-api';
import useBrandConfig from '@shared/composables/useBrandConfig';
import { distanceFromStoreKm } from '@shared/helpers/distanceFromStoreKm';
import { LatLng } from '@shared/types/locations';
import { StoreDetails, StoreDetailsWithDistance } from '@shared/types/stores';
import { usePlaceDetails } from '@unified-commerce/gpc-vue-storefront-google-places';

const getStoreLocation = (stores: StoreDetails[], placeId: string) => {
  const store = stores.find((store) => store.placeId === placeId);
  if (store) {
    return { lat: store.lat, lng: store.lng };
  }
};

interface Params {
  lat?: number;
  lng?: number;
  placeId?: string;
}

export interface UseNearbyStoresConfig {
  storesList: { [state: string]: StoreDetails[] };
}

const useNearbyStores = (
  id: string,
): {
  getNearbyStores: (params: Params) => Promise<StoreDetailsWithDistance[]>;
  error: Ref<unknown | null>;
} => {
  const { composableConfig } = useBrandConfig();
  const { storesList } = composableConfig.useNearbyStores;
  const { getPlaceDetails, result, error } = usePlaceDetails(id);

  const stores: StoreDetails[] = Object.values(storesList).flat();

  const getNearbyStores = async ({ lat, lng, placeId }: Params) => {
    if (!lat && !lng && !placeId) {
      return [];
    }
    let location = lat && lng ? { lat, lng } : placeId && getStoreLocation(stores, placeId);
    if (!location && placeId) {
      await getPlaceDetails(placeId);

      if (result?.value?.result?.geometry?.location) {
        const { lat, lng } = result.value.result.geometry.location;
        location = { lat, lng };
      }
    }
    if (!location || error.value) {
      return [];
    }

    return Object.values(stores)
      .flatMap((store) => {
        return {
          ...store,
          distance: distanceFromStoreKm(location as LatLng, store),
        };
      })
      .filter((store) => store.distance <= 50);
  };

  return { getNearbyStores, error };
};

export default useNearbyStores;
